import { FormEvent, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { api } from '../../../../lib/api';
import { useSite } from '../../../../lib/site-context';
import { SUGGESTED_KEYS } from './Dados';

import './Module.scss';

/* ==========================================================================
   TIPAGENS E SUGESTÕES DE CHAVES
   ========================================================================== */

export interface SettingRegisterItem {
  id?: number | string;
  setting_key: string;
  name: string;
  value: string;
  comment?: string;
  activated: boolean | number;
}



/* ==========================================================================
   COMPONENTE: Editor de Configurações, Identidade e Registros
   ========================================================================== */

export function SettingsEditor() {
  const { site, refresh } = useSite();
  const [items, setItems] = useState<SettingRegisterItem[]>([]);
  const [editingNew, setEditingNew] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string>('');

  // Formulário do novo item
  const [newKey, setNewKey] = useState<string>('');
  const [newName, setNewName] = useState<string>('');
  const [newValue, setNewValue] = useState<string>('');
  const [newComment, setNewComment] = useState<string>('');
  const [newActivated, setNewActivated] = useState<boolean>(true);

  // Carrega e normaliza os dados combinando settings e registros
  useEffect(() => {
    if (site?.settings) {
      const parsed: SettingRegisterItem[] = Object.entries(site.settings).map(([key, val]) => {
        const suggestion = SUGGESTED_KEYS.find((s) => s.key === key);
        return {
          setting_key: key,
          name: suggestion ? suggestion.label : key.replace(/_/g, ' ').toUpperCase(),
          value: typeof val === 'string' ? val : JSON.stringify(val),
          comment: suggestion ? suggestion.defaultComment : '',
          activated: 1,
        };
      });
      setItems(parsed);
    }
  }, [site?.settings]);

  // Preenche nome e comentário automaticamente ao escolher chave sugerida
  function handleKeySelection(selectedKey: string) {
    setNewKey(selectedKey);
    const suggestion = SUGGESTED_KEYS.find((s) => s.key === selectedKey);
    if (suggestion) {
      if (!newName) setNewName(suggestion.label);
      if (!newComment) setNewComment(suggestion.defaultComment);
    }
  }

  /**
   * Salva alterações de um item existente
   */
  async function handleSaveItem(item: SettingRegisterItem): Promise<void> {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await api.saveSetting(item.setting_key, item.value, { name: item.name, comment: item.comment, activated: Boolean(item.activated) });
      setMessage(`Configuração "${item.name || item.setting_key}" salva com sucesso.`);
      if (typeof refresh === 'function') await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Não foi possível salvar a configuração.');
    } finally {
      setSaving(false);
    }
  }

  /**
   * Alterna estado ativado / desativado
   */
  async function handleToggleActive(item: SettingRegisterItem): Promise<void> {
    const nextStatus = item.activated ? 0 : 1;
    try {
      await api.saveSetting(item.setting_key, item.value, { name: item.name, comment: item.comment, activated: Boolean(item.activated) });
      setItems((prev) =>
        prev.map((it) => (it.setting_key === item.setting_key ? { ...it, activated: nextStatus } : it))
      );
      setMessage(`Configuração "${item.setting_key}" ${nextStatus ? 'ativada' : 'desativada'}.`);
      if (typeof refresh === 'function') await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao alterar status.');
    }
  }

  /**
   * Cria nova configuração com metadados
   */
  async function handleCreateNew(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!newKey.trim()) return;

    setSaving(true);
    setMessage('');
    setError('');

    try {
      await api.saveSetting(newKey.trim(), newValue, { name: newName.trim() || newKey.trim(), comment: newComment.trim(), activated: newActivated });
      setItems((prev) => [
        ...prev,
        {
          setting_key: newKey.trim(),
          name: newName.trim() || newKey.trim(),
          value: newValue,
          comment: newComment.trim(),
          activated: newActivated ? 1 : 0,
        },
      ]);

      // Reset
      setNewKey('');
      setNewName('');
      setNewValue('');
      setNewComment('');
      setNewActivated(true);
      setEditingNew(false);
      setMessage('Configuração registrada com sucesso.');
      if (typeof refresh === 'function') await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível salvar a nova configuração.');
    } finally {
      setSaving(false);
    }
  }

  /**
   * Exclui chave do banco
   */
  async function handleDelete(key: string): Promise<void> {
    if (!window.confirm(`Deseja realmente excluir a configuração "${key}"?`)) return;

    setMessage('');
    setError('');
    try {
      await api.removeSetting(key);
      setItems((prev) => prev.filter((it) => it.setting_key !== key));
      setMessage(`Configuração "${key}" removida.`);
      if (typeof refresh === 'function') await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Não foi possível excluir.');
    }
  }

  const [telegram, setTelegram] = useState({
    configured: false, tokenConfigured: false, webhookConfigured: false,
    enabled: true, notifyContact: true, notifyError: true, chatIdConfigured: false,
  });
  const [telegramChatId, setTelegramChatId] = useState('');
  const [telegramSaving, setTelegramSaving] = useState(false);
  const [dbHealth, setDbHealth] = useState<{ ok: boolean; database: string; latencyMs: number } | null>(null);

  async function loadDbHealth(): Promise<void> {
    try {
      const result = await api.health();
      setDbHealth(result);
    } catch {
      setDbHealth(null);
    }
  }

  async function loadTelegram(): Promise<void> {
    try {
      const response = await fetch('/api/admin/integrations/telegram', { credentials: 'same-origin' });
      if (!response.ok) return;
      const data = await response.json() as typeof telegram;
      setTelegram(data);
    } catch (error) {
      console.error('[A3GS] Falha ao carregar status do Telegram:', error);
    }
  }

  async function saveTelegram(): Promise<void> {
    setTelegramSaving(true);
    try {
      const response = await fetch('/api/admin/integrations/telegram', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ...telegram, chatId: telegramChatId || undefined }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error || 'Não foi possível salvar o Telegram.');
      }
      setMessage('Configuração do Telegram salva.');
      await loadTelegram();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Erro ao salvar Telegram.');
    } finally {
      setTelegramSaving(false);
    }
  }

  useEffect(() => { void loadTelegram(); void loadDbHealth(); }, []);

  return (
    <section className="admin-section" aria-labelledby="settings-title">
      <div className="admin-section-head">
        <div>
          <span className="section-tag">CONFIGURAÇÃO & METADADOS</span>
          <h2 id="settings-title">Parâmetros, Identidade e SEO</h2>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setEditingNew(!editingNew)}
        >
          <Icon icon={editingNew ? 'lucide:x' : 'lucide:plus'} width="16" height="16" />
          <span>{editingNew ? 'Fechar' : 'Nova Configuração'}</span>
        </button>
      </div>

      {message && <p className="form-info" role="status">{message}</p>}
      {error && <p className="form-error" role="alert">{error}</p>}

      <section className="admin-editor" aria-labelledby="database-title">
        <h3 id="database-title">Banco de dados e ambiente</h3>
        <p className="muted">
          A seleção real do banco é feita pelo binding/variável de ambiente do Cloudflare. O painel
          mostra o estado atual, mas não grava credenciais de banco no CMS.
        </p>
        <div className="form-info" role="status">
          {dbHealth?.ok
            ? `Conectado: ${dbHealth.database.toUpperCase()} · ${dbHealth.latencyMs} ms`
            : 'Não foi possível confirmar a conexão agora.'}
        </div>
        <button type="button" className="btn btn-outline" onClick={() => void loadDbHealth()}>
          Testar conexão
        </button>
      </section>

      <section className="admin-editor" aria-labelledby="telegram-title">
        <h3 id="telegram-title">Integração com Telegram</h3>
        <p className="muted">
          O Bot Token e o segredo do webhook ficam exclusivamente nos Secrets do Cloudflare.
          Nunca coloque esses valores em <code>site_settings</code>.
        </p>
        <div className="admin-form-grid">
          <label className="form-switch">
            <input type="checkbox" checked={telegram.enabled} onChange={(e) => setTelegram((v) => ({ ...v, enabled: e.target.checked }))} />
            <span>Telegram ativo</span>
          </label>
          <label className="form-switch">
            <input type="checkbox" checked={telegram.notifyContact} onChange={(e) => setTelegram((v) => ({ ...v, notifyContact: e.target.checked }))} />
            <span>Notificar novos contatos</span>
          </label>
          <label className="form-switch">
            <input type="checkbox" checked={telegram.notifyError} onChange={(e) => setTelegram((v) => ({ ...v, notifyError: e.target.checked }))} />
            <span>Notificar erros técnicos</span>
          </label>
          <label>
            Chat ID
            <input value={telegramChatId} onChange={(e) => setTelegramChatId(e.target.value)} placeholder={telegram.chatIdConfigured ? 'Configurado' : '-100...'} />
          </label>
        </div>
        <p className="form-info" role="status">
          Bot Token: {telegram.tokenConfigured ? 'configurado' : 'não configurado'} ·
          Chat ID: {telegram.chatIdConfigured ? 'configurado' : 'não configurado'} ·
          Webhook Secret: {telegram.webhookConfigured ? 'configurado' : 'não configurado'}
        </p>
        <button type="button" className="btn btn-primary" onClick={() => void saveTelegram()} disabled={telegramSaving}>
          {telegramSaving ? 'Salvando…' : 'Salvar Telegram'}
        </button>
      </section>

      {/* Formulário de Criação Integrado com a Tabela site_register */}
      {editingNew && (
        <form className="admin-editor" onSubmit={handleCreateNew} style={{ marginBottom: '24px' }}>
          <h3>Registrar Novo Parâmetro</h3>
          <div className="admin-form-grid">
            <label>
              Chave Técnica (setting_key)
              <input
                type="text"
                list="suggested-settings"
                required
                placeholder="Ex: social_whatsapp ou crie uma nova"
                value={newKey}
                onChange={(e) => handleKeySelection(e.target.value)}
              />
              <datalist id="suggested-settings">
                {SUGGESTED_KEYS.map((k) => (
                  <option key={k.key} value={k.key}>
                    {k.label}
                  </option>
                ))}
              </datalist>
            </label>

            <label>
              Nome de Exibição (name)
              <input
                type="text"
                required
                placeholder="Ex: WhatsApp Corporativo"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </label>

            <label className="span-2">
              Observação / Descrição de Uso (comment)
              <input
                type="text"
                placeholder="Ex: Utilizado para botão flutuante de atendimento..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
              />
            </label>

            <label className="span-2">
              Valor / Conteúdo (value)
              <textarea
                rows={3}
                required
                placeholder="Texto, código, script ou valor bruto da configuração..."
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
              />
            </label>

            <label style={{ flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={newActivated}
                onChange={(e) => setNewActivated(e.target.checked)}
              />
              <span>Ativado imediatamente</span>
            </label>
          </div>

          <div className="admin-form-actions">
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'Salvando…' : 'Salvar Registro'}
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setEditingNew(false)}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* Grid de Listagem e Edição em Tempo Real */}
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th style={{ width: '28%' }}>Identificação & Chave</th>
              <th>Valor / Conteúdo</th>
              <th style={{ width: '12%', textAlign: 'center' }}>Status</th>
              <th style={{ width: '18%', textAlign: 'right' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center', padding: '24px' }}>
                  Nenhum parâmetro cadastrado.
                </td>
              </tr>
            ) : (
              items.map((item, index) => (
                <tr key={`${item.setting_key}-${index}`}>
                  {/* Identificação (Nome, Chave e Comentário) */}
                  <td>
                    <input
                      type="text"
                      className="dash-input-name"
                      placeholder="Nome do campo"
                      value={item.name}
                      onChange={(e) => {
                        const val = e.target.value;
                        setItems((prev) =>
                          prev.map((it, idx) => (idx === index ? { ...it, name: val } : it))
                        );
                      }}
                    />
                    <small className="dash-code-key">{item.setting_key}</small>
                    <input
                      type="text"
                      className="dash-input-comment"
                      placeholder="Observação (comment)..."
                      value={item.comment ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setItems((prev) =>
                          prev.map((it, idx) => (idx === index ? { ...it, comment: val } : it))
                        );
                      }}
                    />
                  </td>

                  {/* Valor Bruto */}
                  <td>
                    <textarea
                      rows={2}
                      className="dash-textarea-table"
                      value={item.value}
                      onChange={(e) => {
                        const val = e.target.value;
                        setItems((prev) =>
                          prev.map((it, idx) => (idx === index ? { ...it, value: val } : it))
                        );
                      }}
                    />
                  </td>

                  {/* Ativação / Desativação */}
                  <td style={{ textAlign: 'center' }}>
                    <button
                      type="button"
                      className={`dash-status-btn ${Boolean(item.activated) ? 'active' : 'inactive'}`}
                      onClick={() => void handleToggleActive(item)}
                    >
                      <Icon
                        icon={Boolean(item.activated) ? 'lucide:check-circle-2' : 'lucide:x-circle'}
                        width="16"
                        height="16"
                      />
                      <span>{Boolean(item.activated) ? 'Ativo' : 'Inativo'}</span>
                    </button>
                  </td>

                  {/* Ações */}
                  <td style={{ textAlign: 'right' }}>
                    <div className="table-actions" style={{ justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="btn btn-small btn-primary"
                        onClick={() => void handleSaveItem(item)}
                        disabled={saving}
                        title="Salvar alterações"
                      >
                        <Icon icon="lucide:save" width="14" height="14" />
                        <span>Salvar</span>
                      </button>

                      <button
                        type="button"
                        className="btn btn-small btn-danger"
                        onClick={() => void handleDelete(item.setting_key)}
                        title="Excluir parâmetro"
                      >
                        <Icon icon="lucide:trash-2" width="14" height="14" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}